import { MutationTree } from 'vuex'
import { BuilderState } from './types'
import Draft from '@/calendesk/models/DTO/Response/Draft'
import Page from '@/calendesk/models/DTO/Response/Page'
import Section from '@/calendesk/models/DTO/Response/Section'
import { ChangeStep } from '@/calendesk/models/ChangeStep'
import vuetify from '@/plugins/vuetify'
import { BusinessType, PreviewSizeType } from '@/calendesk/models/BuilderTypes'
import WebsiteStyleModel from '@/calendesk/models/WebsiteStyleModel'
import { SaveStatus } from '@/calendesk/models/SaveStatus'

// Each step stores two full draft clones, so an unbounded stack eats memory
// during long editing sessions; 50 undo levels is plenty.
const MAX_CHANGE_STEPS = 50

export const mutations: MutationTree<BuilderState> = {
  SET_DRAFT (state, payload: Draft) {
    state.draft = payload

    vuetify.framework.theme.themes.light.wb_color_primary__color__ = payload.configuration.wb_color_primary__color__
    vuetify.framework.theme.themes.light.wb_color_bg__color__ = payload.configuration.wb_color_bg__color__
    vuetify.framework.theme.themes.light.wb_color_text__color__ = payload.configuration.wb_color_text__color__
    vuetify.framework.theme.themes.light.wb_color_bg_2__color__ = payload.configuration.wb_color_bg_2__color__
    vuetify.framework.theme.themes.light.wb_color_text_2__color__ = payload.configuration.wb_color_text_2__color__
    // Mirrors Renderer.setThemeColors in the client: the input border is not tenant-set.
    vuetify.framework.theme.themes.light.wb_color_input_border__color__ =
      payload.configuration.wb_dark_mode__checkbox__ ? '#ffffff' : '#646464'
  },
  SET_CURRENT_PAGE (state, payload: Page) {
    state.currentPage = payload
  },
  SET_SELECTED_SECTION (state, payload: Section) {
    state.selectedSection = payload
  },
  ADD_TO_STEPS (state, changeStep: ChangeStep) {
    // A new change invalidates the redo branch above the cursor - keeping it
    // would let "redo" resurrect states the user already abandoned.
    state.steps = state.steps.slice(0, state.currentStepIndex)
    state.steps.push(changeStep)

    if (state.steps.length > MAX_CHANGE_STEPS) {
      state.steps = state.steps.slice(state.steps.length - MAX_CHANGE_STEPS)
    }

    state.currentStepIndex = state.steps.length
  },
  CLEAR_FUTURE_STEPS (state) {
    state.steps = state.steps.splice(0, state.currentStepIndex)
  },
  SET_CURRENT_STEP_INDEX (state, index: number) {
    state.currentStepIndex = index
  },
  CLEAR_ALL_STEPS (state) {
    state.steps = []
    state.currentStepIndex = 0
  },
  SET_SELECTED_EXTERNAL_CONFIGURATION (state, selectedExternalConfiguration) {
    state.selectedExternalConfiguration = selectedExternalConfiguration
  },
  SET_PREVIEW_TYPE (state, previewType: PreviewSizeType) {
    state.previewType = previewType
  },
  SET_EDITED_CONFIGURATION_KEY (state, editedConfigurationKey: string | null) {
    state.editedConfigurationKey = editedConfigurationKey
  },
  SET_BUSINESS_TYPE (state, businessType: BusinessType) {
    state.businessType = businessType
  },
  SET_WEBSITE_STYLE_MODEL (state, websiteStyleModel: WebsiteStyleModel) {
    state.websiteStyleModel = websiteStyleModel
  },
  SET_SAVE_STATUS (state, saveStatus: SaveStatus) {
    state.saveStatus = saveStatus
  },
  SET_LAST_SAVED_AT (state, lastSavedAt: number) {
    state.lastSavedAt = lastSavedAt
  }
}
